import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { staffApi, ApiError } from "@/api";
import type { BlockDocumentJson, LineItem, QuoteTemplate } from "@/types";
import { Button, FullPageSpinner } from "@/components/ui";
import { ContentBuilder } from "./ContentBuilder";

const EMPTY_DOC: BlockDocumentJson = { version: 1, blocks: [] };

/** Build a representative preview from the template's default_line_items, which
 *  are line-item-shaped seeds (not real rows). Synthesises ids/line_total so the
 *  pricing block has something to render. */
function previewLineItems(defaults: Array<Partial<LineItem>> | undefined): LineItem[] {
  return (defaults ?? []).map((d, i) => ({
    id: -(i + 1),
    quote_id: 0,
    position: d.position ?? i,
    title: d.title ?? "Line item",
    description: d.description,
    quantity: d.quantity ?? 1,
    unit_price: d.unit_price ?? 0,
    line_total: d.line_total ?? (d.quantity ?? 1) * (d.unit_price ?? 0),
    is_optional: d.is_optional ?? false,
    fee_type: d.fee_type,
    default_selected: d.default_selected ?? false,
    included: d.included ?? null,
    not_included: d.not_included ?? null,
  }));
}

/**
 * Template content editor. Editing a template's blocks means every new quote
 * cloned from it (api 199) starts with those blocks — no rekeying per quote.
 * Loads the template (list + find — there's no single-get endpoint), edits its
 * `content` via the shared ContentBuilder, and saves the whole object via
 * PATCH template/{id} (api 202, full-object update).
 */
export function TemplateBuilder() {
  const { templateId = "" } = useParams();
  const id = Number(templateId);
  const navigate = useNavigate();

  const [template, setTemplate] = useState<QuoteTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    staffApi
      .listTemplates()
      .then((list) => {
        if (cancelled) return;
        const found = list.find((t) => t.id === id) ?? null;
        if (!found) setError("Template not found.");
        setTemplate(found);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load templates.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const initialContent =
    template?.content && Array.isArray(template.content.blocks) ? template.content : EMPTY_DOC;
  const lineItems = useMemo(() => previewLineItems(template?.default_line_items), [template]);

  if (loading) return <FullPageSpinner label="Loading template…" />;
  if (error || !template) {
    return (
      <div className="builder__error-page">
        <p className="builder__error" role="alert">{error ?? "Template not found."}</p>
        <Link to="/staff/templates"><Button variant="secondary">Back to templates</Button></Link>
      </div>
    );
  }

  return (
    <ContentBuilder
      key={id}
      heading={<>Edit template · {template.name}</>}
      initialContent={initialContent}
      lineItems={lineItems}
      warningSubject="template"
      onSave={(doc) =>
        staffApi
          .updateTemplate(id, {
            // PATCH 202 is a full-object update — send the whole template.
            name: template.name,
            description: template.description,
            content: doc,
            default_line_items: template.default_line_items,
            is_active: template.is_active,
          })
          .then(() => undefined)
      }
      onBack={() => navigate("/staff/templates")}
    />
  );
}
